/**
 * Library Sidebar
 * 
 * Entity type navigation sidebar showing all 10 entity types with counts.
 * Includes Export/Import buttons at the bottom.
 */

import React, { useState, useEffect } from 'react';
import { useLibrary, ENTITY_TYPES } from '../LibraryContext';
import libraryService from '../../../services/libraryService';
import { Button } from '../../../components/ui/Button2';
import { Icon } from '../../../components/ui/Icon';
import styles from '../styles/Library.module.css';

export default function LibrarySidebar() {
  const { state, actions } = useLibrary();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch counts for all entity types on mount
  useEffect(() => {
    fetchAllCounts();
  }, []);

  // Refetch counts when currentSection changes (to update after CRUD operations)
  useEffect(() => {
    if (state.counts && Object.keys(state.counts).length > 0) {
      setCounts(state.counts);
    }
  }, [state.counts]);

  const fetchAllCounts = async () => {
    try {
      setLoading(true);
      const countPromises = Object.keys(ENTITY_TYPES).map(async (key) => {
        try {
          const result = await libraryService.getCountByType(key);
          return { key, count: result.count || 0 };
        } catch (error) {
          console.error(`Error fetching count for ${key}:`, error);
          return { key, count: 0 };
        }
      });

      const results = await Promise.all(countPromises);
      const countsObj = results.reduce((acc, { key, count }) => {
        acc[key] = count;
        return acc;
      }, {});

      setCounts(countsObj);
      actions.setCounts(countsObj);
    } catch (error) {
      console.error('Error fetching counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    actions.openExportModal();
  };

  const handleImport = () => {
    actions.openImportModal();
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <h3 className="title-1">LIBRARY</h3>
      </div>

      {Object.entries(ENTITY_TYPES).map(([key, config]) => (
        <div
          key={key}
          className={`${styles.sidebarSection} ${state.currentSection === key ? styles.active : ''}`}
          onClick={() => actions.setCurrentSection(key)}
        >
          <span className={styles.sectionName}>{config.plural}</span>
          <span className={styles.count}>
            {loading ? '...' : counts[key] || 0}
          </span>
        </div>
      ))}

      <div className={styles.sidebarDivider} />

      <div className={styles.sidebarActions}>
        <Button
          variant="outline"
          size="sm"
          className={styles.sidebarBtn}
          startIcon={<Icon name="download" />}
          onClick={handleExport}
        >
          Export
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={styles.sidebarBtn}
          startIcon={<Icon name="upload" />}
          onClick={handleImport}
        >
          Import
        </Button>
      </div>
    </aside>
  );
}
